import React, { useContext, useEffect, useState } from "react";
import PageHeading from "../../Components/PageHeading/PageHeading";
import "./support.css";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import axios from "axios";

export default function Support() {
    const { AIuser } = useContext(UserAuthContext);
    const [formData, setFormData] = useState({
        name: AIuser?.name || "",
        email: AIuser?.email || "",
        message: "",
        attachment: null,
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (AIuser) {
            setFormData((prev) => ({
                ...prev,
                name: prev.name || AIuser.name || "",
                email: prev.email || AIuser.email || "",
            }));
        }
    }, [AIuser]);

    const handleFileChange = (e) => {
        setFormData({
            ...formData,
            attachment: e.target.files[0]
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = "Name is required";

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        if (!formData.message.trim()) {
            newErrors.message = "Message is required";
        } else if (formData.message.trim().length < 10) {
            newErrors.message = "Message must be at least 10 characters long";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);

            let submitData;
            let headers = {};

            if (formData.attachment) {
                submitData = new FormData();
                submitData.append("name", formData.name);
                submitData.append("email", formData.email);
                submitData.append("message", formData.message);
                submitData.append("attachment", formData.attachment);
                headers = { 'Content-Type': 'multipart/form-data' };
            } else {
                submitData = {
                    name: formData.name,
                    email: formData.email,
                    message: formData.message
                };
            }

            axios.post("https://backend.whalesai.com/api/v1/support", submitData, { headers })
                .then(() => {
                    setIsSubmitting(false);
                    setIsSuccess(true);
                    setFormData(prev => ({ ...prev, message: "", attachment: null }));
                })
                .catch(() => {
                    setIsSubmitting(false);
                    setIsSuccess(false);
                });
        }
    };

    return (
        <div>
            <PageHeading
                title="Support"
                description="How can we help you? Send us a message or check our FAQ."
            />

            <section className="support-section">
                <div className="support-container">

                    {/* Contact Form */}
                    <div className="support-form-card">
                        {isSuccess ? (
                            <div className="support-success-message">
                                <div className="support-success-icon">
                                    <i className="fa-solid fa-check"></i>
                                </div>
                                <h3>Message Sent Successfully!</h3>
                                <p>Thank you for reaching out. Our support team will get back to you as soon as possible.</p>
                                <button
                                    className="support-another-btn"
                                    onClick={() => setIsSuccess(false)}
                                >
                                    Send Another Message
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="support-form-title">Send us a message</h3>
                                <p className="support-form-subtitle">If you feel that any of the content does not meet your standards or expectations, please write to us and let us know. Our team will be happy to review it and generate improved content tailored to your requirements.</p>

                                <form onSubmit={handleSubmit}>
                                    <div className="support-form-row">
                                        <div className="support-form-group">
                                            <label htmlFor="name">Your Name <span className="required-star">*</span></label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                placeholder="Enter Name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={errors.name ? "field-error" : ""}
                                                disabled={isSubmitting}
                                            />
                                            {errors.name && <div className="support-field-error">{errors.name}</div>}
                                        </div>

                                        <div className="support-form-group">
                                            <label htmlFor="email">Email Address <span className="required-star">*</span></label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="Enter Email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={errors.email ? "field-error" : ""}
                                                disabled={isSubmitting}
                                            />
                                            {errors.email && <div className="support-field-error">{errors.email}</div>}
                                        </div>
                                    </div>

                                    <div className="support-form-group">
                                        <label htmlFor="message">Message <span className="required-star">*</span></label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            placeholder="How can we help you today?"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className={errors.message ? "field-error" : ""}
                                            disabled={isSubmitting}
                                        ></textarea>
                                        {errors.message && <div className="support-field-error">{errors.message}</div>}
                                        <div className="support-char-count">
                                            {formData.message.length} characters
                                        </div>
                                    </div>

                                    <div className="support-form-group">
                                        <label htmlFor="attachment">Attachment <span className="optional-text">(Optional)</span></label>
                                        <input
                                            type="file"
                                            id="attachment"
                                            name="attachment"
                                            onChange={handleFileChange}
                                            className="support-file-input"
                                            disabled={isSubmitting}
                                            accept="image/*, video/*, .pdf,.doc,.docx"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="support-submit-btn"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <span><i className="fa-solid fa-circle-notch fa-spin me-2"></i> Sending...</span>
                                        ) : (
                                            <span>Send Message <i className="fa-regular fa-paper-plane ms-1"></i></span>
                                        )}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>

                </div>
            </section>
        </div>
    );
}